import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Callout } from '@client/components/_ui/feedback/Callout';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { message } from '@client/components/_ui/feedback/message';
import { confirmDestructive } from '@client/components/_ui/feedback/modal';
import { ScheduledAttestationForm } from '@client/components/attestations/scheduled/ScheduledAttestationForm';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { Typography } from 'antd';
import { useId, useRef, useState } from 'react';

const { Text } = Typography;

/** How often a locked ("sending now") schedule is re-read; a send takes seconds, a lease at most ten minutes. */
const RUNNING_POLL_MS = 15_000;

export const Route = createFileRoute('/console/$orgId/attestations/scheduled-attestations/$scheduleId')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Edit scheduled attestation',
        },
        permissions: ['user_attestation:update'],
        featureFlag: 'enable-scheduled-attestations',
    },
    params: {
        parse({ scheduleId }) {
            // A non-numeric segment is a 404, not a BigInt SyntaxError for Sentry.
            if (!/^\d+$/.test(scheduleId)) throw notFound();
            return { scheduleId: BigInt(scheduleId) };
        },
        stringify({ scheduleId }) {
            return { scheduleId: String(scheduleId) };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <EditScheduledAttestation />
        </PageController>
    );
}

function EditScheduledAttestation() {
    const formId = useId();
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();
    const { scheduleId } = Route.useParams();
    const onCloseRef = useRef<(() => void) | null>(null);
    const [isValid, setIsValid] = useState(false);

    const {
        data: schedule,
        isLoading,
        error,
        refetch,
        isRefetching,
    } = trpc.userAttestation.getScheduledUserAttestation.useQuery(
        { id: scheduleId },
        {
            meta: { errorSurface: 'inline' },
            // While the sweep is sending this schedule the form is locked; poll so
            // the lock clears on its own once the send (or its lease) is over.
            refetchInterval: (query) => (query.state.data?.isRunning ? RUNNING_POLL_MS : false),
        },
    );

    // The version the editor started from. A later version means the record
    // changed elsewhere: the form is remounted on it (below) and told so.
    const version = schedule ? `${schedule.id}-${new Date(schedule.updatedAt).getTime()}` : null;
    const openedVersionRef = useRef<string | null>(null);
    // Set by a successful save here: the version bump that follows is the
    // editor's own, not a change elsewhere (the overlay is closing anyway).
    const savedHereRef = useRef(false);
    if (version !== null && openedVersionRef.current === null) openedVersionRef.current = version;
    const reloadedElsewhere =
        !savedHereRef.current &&
        version !== null &&
        openedVersionRef.current !== null &&
        openedVersionRef.current !== version;

    const invalidateList = () => {
        void trpcUtils.userAttestation.listScheduledUserAttestations.invalidate();
    };
    const invalidate = () => {
        invalidateList();
        void trpcUtils.userAttestation.getScheduledUserAttestation.invalidate({ id: scheduleId });
    };

    const { mutate: update, isPending: isSaving } = trpc.userAttestation.updateScheduledUserAttestation.useMutation({
        onSuccess: () => {
            savedHereRef.current = true;
            message.success('Scheduled attestation saved');
            invalidate();
            onCloseRef.current?.();
        },
        onError: (error) => {
            message.error(error.message || 'Failed to save scheduled attestation');
        },
    });

    const { mutate: remove, isPending: isDeleting } = trpc.userAttestation.deleteScheduledUserAttestation.useMutation({
        onSuccess: () => {
            message.success('Scheduled attestation deleted');
            // The list only: the row is gone, and refetching its detail while the
            // overlay closes would just fetch a NOT_FOUND into the closing view.
            invalidateList();
            onCloseRef.current?.();
        },
        onError: (error) => {
            message.error(error.message || 'Failed to delete scheduled attestation');
        },
    });

    return (
        <OverlayPage
            title="Edit scheduled attestation"
            size="large"
            afterClose={() => navigate({ moveInMatches: -1 })}
            footer={({ onClose }) => (
                <>
                    {schedule && (
                        <Button
                            type="danger"
                            className="mr-auto"
                            loading={isDeleting}
                            onClick={() =>
                                confirmDestructive({
                                    title: 'Delete this schedule?',
                                    content: "Future sends stop. Attestations already sent aren't affected.",
                                    okText: 'Delete',
                                    onOk: () => {
                                        onCloseRef.current = onClose;
                                        remove({ id: scheduleId });
                                    },
                                })
                            }
                        >
                            Delete schedule
                        </Button>
                    )}
                    <Button type="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    {/* Title case mandated by CUR-858 — an exception to the sentence-case rule. */}
                    <Button
                        type="primary"
                        htmlType="submit"
                        form={formId}
                        loading={isSaving}
                        disabled={!schedule || !isValid}
                    >
                        Save Scheduled Attestation
                    </Button>
                </>
            )}
        >
            {({ onClose }) =>
                isLoading ? (
                    <Loading />
                ) : error && !schedule ? (
                    <InlineQueryError error={error} onRetry={() => refetch()} retrying={isRefetching} />
                ) : !schedule ? (
                    <Text type="secondary">Scheduled attestation not found</Text>
                ) : (
                    <>
                        {/* A background refresh failed but the record is still cached: the
                            form (and any draft in it) stays; the failure is shown beside it. */}
                        {error && (
                            <InlineQueryError
                                error={error}
                                onRetry={() => refetch()}
                                retrying={isRefetching}
                                title="The latest version of this schedule could not be loaded"
                                compact
                                className="mb-4"
                            />
                        )}
                        {reloadedElsewhere && (
                            <Callout variant="info" title="This schedule was changed elsewhere" className="mb-4">
                                The form has been reloaded with the latest version. Any edits you had not saved were
                                discarded.
                            </Callout>
                        )}
                        <ScheduledAttestationForm
                            // A cached row renders at once and the background refetch may
                            // replace it (someone else disabled it, say). The form seeds
                            // its fields only when it mounts, so a new record version
                            // remounts it rather than leaving stale values to be saved.
                            key={version ?? undefined}
                            mode="edit"
                            formId={formId}
                            schedule={schedule}
                            onValidityChange={setIsValid}
                            onSubmit={(input) => {
                                onCloseRef.current = onClose;
                                update({ id: scheduleId, ...input });
                            }}
                        />
                    </>
                )
            }
        </OverlayPage>
    );
}
